import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { WAITING_REASONS, answerRows, categoryById, fieldsFor, typeById } from '../demo/catalog.js';
import { useDemo } from '../demo/DemoContext.jsx';
import BackButton from '../components/BackButton.jsx';
import SeatAssessmentPanel from '../components/SeatAssessmentPanel.jsx';
import { formatTarget } from '../demo/sla.js';
import {
  CommandError, acknowledgeTicket, getTicketResult, listAssignees, postMessage, refresh, resolveTicket, runCommand, staffName, statusText, titleFor, updateTicket,
} from '../demo/store.js';
import { keyForCommand, wordsForCommandError } from '../lib/commandKey.js';
import { Avatar, Chip, Pri, ServerNotice, SourceChip, StatusChip } from '../components/Chips.jsx';
import { followUpHref, reopenState, slaFor } from '../lib/ticketView.js';
import { WALKTHROUGH_WRITE_BLOCKED } from '../lib/liveWrites.js';
import { markSeen } from '../lib/unread.js';
import { clockTime, labelFor, since, slaTone, stamp } from '../lib/tone.js';
import { listFiles, sendFiles, wordsForFailed } from '../lib/ticketFiles.js';
import { FileList, FilePicker } from '../components/TicketFiles.jsx';

/* One ticket, two audiences.
 *
 * The IT view carries internal notes, the assignment controls and the priority
 * override. The requester's view of the same thread has the notes removed.
 * Filtering happens here AND in the API in the real build — a note has to leak
 * past two separate guards before it reaches the person it is about.
 */

// Events the requester is allowed to see. Assignment and first-response
// bookkeeping are IT's business; the rule that set the priority is not.
const REQUESTER_EVENTS = new Set(['priority', 'status', 'resolve']);

// What the screen is SHOWING decides which store an action touches (HD-02, review of #72).
const sourceOf = (t) => (t.fromServer ? 'real' : 'demo');

/* A filed ticket takes the staff commands only when the server says so (canCommand). A demo
 * ticket always works, as before. */
const canTriage = (t) => !t.fromServer || t.canCommand === true;

/* Runs one staff command on a filed ticket and keeps what the screen needs: whether it is busy
 * and the plain sentence for a failure. The same press sent again after an unconfirmed attempt
 * carries the same key, so it is never done twice (lib/commandKey.js). */
function useCommands(ticket) {
  const memory = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const run = async (command, args) => {
    setBusy(true);
    setError(null);
    const intent = { command, args, expectedVersion: ticket.version };
    const next = keyForCommand(memory.current, intent);
    memory.current = next.memory;
    try {
      await runCommand(ticket.number, command, args, { expectedVersion: ticket.version, operationKey: next.key });
      memory.current = null;
      return true;
    } catch (err) {
      if (err instanceof CommandError) {
        if (err.outcome === 'rejected') memory.current = null;
        setError(wordsForCommandError(err));
        if (err.code === 'STALE') refresh();
      } else {
        // Not a server answer at all: the walkthrough build refuses live writes before sending,
        // and says so in a plain sentence.
        setError(err?.code === WALKTHROUGH_WRITE_BLOCKED ? err.message : 'Something went wrong before the change was sent. Nothing was changed.');
      }
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { run, busy, error };
}

const CommandNotice = ({ error }) => (error ? (
  <p className="why" role="alert" style={{ marginTop: 6 }}>{error}</p>
) : null);

/* A real ticket is on screen but the latest re-read of it failed. The draft and the
 * ticket stay; the person is told, and after a denial nothing can be sent. */
function StaleNotice({ problem }) {
  const text = {
    unavailable: 'Could not refresh this ticket, so what you see may be out of date. Anything you typed is kept.',
    forbidden: 'You no longer have access to this ticket. Anything you typed is kept below but cannot be sent.',
    notfound: 'This ticket could not be found any more. Anything you typed is kept below but cannot be sent.',
  }[problem];
  return (
    <div className="banner banner--bad" role="alert" style={{ marginTop: 10 }}>
      <span className="material-symbols-outlined" aria-hidden="true">error</span>
      <span>{text}</span>
      <button type="button" className="btn btn--quiet" onClick={() => refresh()}>Check again</button>
    </div>
  );
}

export default function TicketDetail() {
  const { number } = useParams();
  const { me, meStaffId, isIT, reference, version, walkthrough } = useDemo();
  const [ticket, setTicket] = useState(undefined);
  // Why there is no ticket: 'notfound' | 'forbidden' | 'unavailable'. These are
  // three different things and the page says which (HD-02, #53).
  const [problem, setProblem] = useState(null);
  // A real ticket stays on screen but its latest re-read failed: the same three
  // reasons. Never silent (review of #72, finding 3).
  const [stale, setStale] = useState(null);
  const [resolving, setResolving] = useState(false);
  // The server answered CLOSED to a reply on this ticket: it is closed, whatever this browser's clock
  // says about the reopen window (review of #107, F2). Cleared when the page moves to another ticket.
  const [closedByServer, setClosedByServer] = useState(false);
  // Bumped when the reopen window ends while the page is open, so the page re-draws and stops offering
  // "Still not fixed" on time (review of #107, F1).
  const [, setWindowTick] = useState(0);
  const shown = useRef(undefined);
  shown.current = ticket;
  // The files on a filed ticket (S09), as the server lists them for this reader: null until read,
  // and on a demo ticket. Read again whenever the store says something changed.
  const [files, setFiles] = useState(null);

  // A different ticket is a different page: nothing of the old one carries over.
  useEffect(() => {
    shown.current = undefined;
    setTicket(undefined);
    setProblem(null);
    setStale(null);
    setClosedByServer(false);
    setFiles(null);
  }, [number]);

  const fromServer = Boolean(ticket?.fromServer);
  useEffect(() => {
    if (!fromServer) return undefined;
    let cancelled = false;
    listFiles(number).then((list) => {
      // A list that could not be read leaves the last one on screen rather than blanking it.
      if (!cancelled && list) setFiles(list);
    });
    return () => {
      cancelled = true;
    };
  }, [number, version, fromServer]);

  const reopenUntil = ticket?.reopenUntil;
  useEffect(() => {
    const left = reopenUntil ? new Date(reopenUntil).getTime() - Date.now() : NaN;
    if (!(left > 0) || left > 2 ** 31 - 1) return undefined;
    const id = setTimeout(() => setWindowTick((n) => n + 1), left + 1000);
    return () => clearTimeout(id);
  }, [reopenUntil]);

  // Resolved or closed from elsewhere while the resolve panel was open: the panel goes away.
  const ticketStatus = ticket?.status;
  useEffect(() => {
    if (['resolved', 'closed'].includes(ticketStatus)) setResolving(false);
    // IT reopened it while the page was open: the server's earlier CLOSED no longer holds.
    else if (ticketStatus) setClosedByServer(false);
  }, [ticketStatus]);

  // The thread last marked seen: "number:message count". Each store write re-reads the
  // ticket; only a new ticket or a new message is worth a /seen (each is a warehouse
  // write). A reply of your own still sends two: postMessage marks it too, so the
  // watermark moves even when this re-read fails. Neither forces a new unread read.
  const seenKey = useRef(null);

  useEffect(() => {
    let cancelled = false;
    getTicketResult(number)
      // The store never rejects, but a screen that waits forever on one that does
      // is worse than one that says "could not load" (review of #72, finding 2).
      .catch(() => ({ status: 'unavailable', ticket: null }))
      .then((r) => {
        if (cancelled) return;
        const prev = shown.current;
        const keep = Boolean(prev && prev.fromServer && prev.number === number);

        // A REAL ticket is on screen. A failed re-read - or the demo ticket that
        // happens to share its number - must not replace it: the composer, its
        // draft and its retry id live under this page, and the displayed source
        // must stay what the actions will act on (review of #65 R1, #72 finding 1).
        let status = r.status;
        if (status === 'ok' && keep && !r.ticket.fromServer) {
          status = r.ticket.serverUnavailable ? 'unavailable' : 'notfound';
        }
        if (status !== 'ok') {
          if (keep) {
            setStale(status);
          } else {
            setTicket(null);
            setProblem(status);
            setStale(null);
          }
          return;
        }
        setStale(null);
        setProblem(null);
        const t = r.ticket;
        setTicket(keep && t.threadError ? { ...t, messages: prev.messages, canNote: prev.canNote } : t);
        // The thread is on screen, so its new replies are read (S10 part B). Not when the
        // thread could not be read: the person has not seen it, and the mark must stay.
        if (t.fromServer && !t.threadError) {
          const key = `${number}:${t.messages?.length ?? 0}`;
          if (seenKey.current !== key) {
            seenKey.current = key;
            markSeen(number);
          }
        }
      });
    return () => {
      cancelled = true;
    };
  }, [number, version]);

  const sla = useMemo(() => slaFor(ticket, reference), [ticket, reference]);

  if (ticket === undefined) return <p className="muted" style={{ paddingTop: 34 }}>Loading…</p>;
  if (ticket === null) {
    const copy = {
      forbidden: ['You do not have access to ' + number, 'This ticket exists but is not yours to open. If you think that is wrong, ask IT.'],
      unavailable: ['Could not load ' + number, `${walkthrough ? 'Real tickets' : 'Tickets'} cannot be reached right now. This does not mean the ticket is gone. Try again in a moment.`],
      // The live app has no demo tickets and no Reset (S08 part B), so it does not mention them.
      notfound: ['No ticket ' + number, `There is no ticket with that number.${walkthrough ? ' A demo ticket may have been cleared by Reset.' : ''}`],
    }[problem || 'notfound'];
    return (
      <div className="empty" style={{ marginTop: 34 }}>
        <p className="empty__title">{copy[0]}</p>
        <p className="empty__body">{copy[1]}</p>
        {problem === 'unavailable' ? (
          <button type="button" className="btn btn--quiet" onClick={() => refresh()}>Check again</button>
        ) : null}
      </div>
    );
  }

  // Messages and events on one timeline, so the story of the ticket reads in a
  // single pass instead of being split between a thread and a history panel.
  const timeline = [
    ...ticket.messages
      .filter((m) => isIT || !m.isInternal)
      .map((m) => ({ kind: 'msg', at: m.at, data: m })),
    ...ticket.events
      .filter((e) => isIT || REQUESTER_EVENTS.has(e.type))
      .map((e) => ({ kind: 'sys', at: e.at, data: e })),
  ].sort((a, b) => new Date(a.at) - new Date(b.at));

  const type = typeById(ticket.requestTypeId);
  const standing = closedByServer ? 'closed' : reopenState(ticket);

  return (
    <>
      <section className="hero" style={{ paddingBottom: 0 }}>
        <BackButton to={isIT ? '/queue' : '/mine'}>{isIT ? 'Back to queue' : 'Back to my tickets'}</BackButton>
        <div className="crumb">
          <span className="material-symbols-outlined" aria-hidden="true">home</span>
          <Link to={isIT ? '/queue' : '/mine'}>{isIT ? 'Queue' : 'My tickets'}</Link>
          <span>›</span>
          <span>{ticket.number}</span>
        </div>
      </section>

      <div className="detail">
        <div className="detail__head">
          <div className="ref" style={{ fontSize: 11.5 }}>{ticket.number} <SourceChip ticket={ticket} /></div>
          <h1>{titleFor(ticket)}</h1>
          <div className="detail__meta">
            <StatusChip ticket={ticket} />
            <Pri priority={ticket.priority} ruleName={ticket.priorityRuleName} />
            <Chip tone="flat">{categoryById(ticket.categoryId)?.label}</Chip>
            <Chip tone="flat">{ticket.requester.brand}</Chip>
            {/* Raised for someone else: BOTH people see who typed it, so the person never finds a
                ticket they did not raise with no word on where it came from. */}
            {ticket.raisedByName ? (
              <Chip tone="flat">For {ticket.requester.name} · raised by {ticket.raisedByName}</Chip>
            ) : isIT ? (
              <Chip tone="flat">Raised by {ticket.requester.name} · {ticket.requester.role}</Chip>
            ) : null}
            {ticket.similarCount > 1 ? (
              <Chip tone="info" icon="groups">{ticket.similarCount} reports</Chip>
            ) : null}
          </div>
          {isIT && ticket.fromServer && ticket.priority === 'P1' && (ticket.acknowledgedAt || !['resolved', 'closed'].includes(ticket.status)) ? (
            <div style={{ marginTop: 12 }}><AckBar ticket={ticket} /></div>
          ) : null}
        </div>

        <div className="detail__thread">
          <Details ticket={ticket} type={type} files={files ? files.filter((f) => !f.messageId) : null} />

          {isIT && ticket.summary ? (
            <Summary summary={ticket.summary} reopened={!['resolved', 'closed'].includes(ticket.status)} />
          ) : null}

          {ticket.serverUnavailable ? (
            <ServerNotice state="unavailable" />
          ) : null}
          {stale ? <StaleNotice problem={stale} /> : null}

          <div className="section__head" style={{ marginTop: 22 }}>
            <h2 className="section__title">Conversation</h2>
          </div>

          {ticket.threadError ? (
            <div className="banner" role="alert" style={{ marginTop: 10 }}>
              <span className="material-symbols-outlined" aria-hidden="true">error</span>
              <span>
                The conversation could not be loaded, so what you see here may be missing
                messages.
              </span>
              <button type="button" className="btn btn--quiet" onClick={() => refresh()}>
                Check again
              </button>
            </div>
          ) : null}

          {timeline.length === 0 && !ticket.threadError ? (
            <div className="empty">
              <p className="empty__title">Nothing said yet</p>
              <p className="empty__body">
                {isIT
                  ? 'Nobody has replied to this yet. The first reply stops the response clock.'
                  : "IT has not replied yet. You'll get an email the moment they do."}
              </p>
            </div>
          ) : (
            timeline.map((item) =>
              item.kind === 'sys' ? (
                <SysLine key={item.data.id} event={item.data} />
              ) : (
                <Message key={item.data.id} msg={item.data} ticket={ticket} me={me} isIT={isIT} files={files ? files.filter((f) => f.messageId === item.data.id) : null} />
              )
            )
          )}

          {!isIT ? <RequesterStanding ticket={ticket} standing={standing} /> : null}

          {resolving ? (
            <ResolvePanel ticket={ticket} me={me} onDone={() => setResolving(false)} />
          ) : (
            // Keyed by source: a draft written against the demo ticket must never
            // be sent to the real one with the same number, or the other way round.
            <Composer
              key={sourceOf(ticket)}
              ticket={ticket}
              isIT={isIT}
              me={me}
              denied={stale === 'forbidden' || stale === 'notfound'}
              standing={standing}
              onClosed={() => setClosedByServer(true)}
            />
          )}
        </div>

        <aside className="rail">
          <div className="rail__label">Status</div>
          <div className="rail__value">{statusText(ticket)}</div>

          <div className="rail__label">Priority</div>
          {isIT ? (
            <PriorityControl ticket={ticket} me={me} />
          ) : (
            <div className="rail__value"><Pri priority={ticket.priority} /></div>
          )}
          <div className="why">
            <b>Set by rule:</b> “{ticket.priorityRuleName || 'default'}”.
            {ticket.priorityOverride
              ? ` Changed from ${ticket.priorityOverride.from} by ${ticket.priorityOverride.by} — “${ticket.priorityOverride.reason}”`
              : isIT
                ? " Change it and you'll be asked why — that reason trains the rules."
                : ''}
          </div>

          {isIT ? (
            <>
              <div className="rail__label">Assignee</div>
              <AssigneeControl ticket={ticket} me={me} meStaffId={meStaffId} reference={reference} />
            </>
          ) : (
            <>
              <div className="rail__label">Who has it</div>
              <div className="rail__value">
                {staffName(ticket.assignee) || <span className="muted">Not picked up yet</span>}
              </div>
            </>
          )}

          {sla ? (
            <>
              <div className="rail__label">Response target</div>
              <SlaMeter standing={sla.response} policy={sla.policy} which="responseMins" paused={ticket.status === 'waiting'} />
              <div className="rail__label">Resolution target</div>
              <SlaMeter standing={sla.resolve} policy={sla.policy} which="resolveMins" paused={ticket.status === 'waiting'} />
              <div className="why">
                {ticket.status === 'waiting'
                  ? 'Paused while this is on hold — blocked time never counts against IT.'
                  : sla.policy.aroundClock
                    ? 'Runs around the clock. P1 does not wait for Monday.'
                    : "Counts IT's hours only: 07:00–17:00, Mon–Fri."}
              </div>
            </>
          ) : null}

          <FollowUpLinks ticket={ticket} />

          {isIT ? (
            <>
              <div className="rail__label">Requester</div>
              <div className="rail__value">
                {ticket.requester.name}
                <small>
                  {ticket.requester.role} · {ticket.requester.brand}
                  <br />
                  {ticket.requester.department}
                </small>
              </div>
              {ticket.raisedByName ? (
                <>
                  <div className="rail__label">Raised by</div>
                  <div className="rail__value">{ticket.raisedByName}</div>
                </>
              ) : null}

              <div className="rail__label">Actions</div>
              {ticket.fromServer && !ticket.canCommand ? (
                <p className="why" role="note" style={{ marginTop: 0 }}>
                  Taking, assigning, putting on hold, resolving and reopening are not turned on yet
                  for filed tickets, so they are switched off rather than pretending to work. You
                  can still reply and add notes.
                </p>
              ) : ticket.fromServer && !ticket.lifecycleReady ? (
                <p className="why" role="note" style={{ marginTop: 0 }}>
                  Putting on hold, resolving and reopening are not turned on yet for filed tickets,
                  so they are switched off. You can still take it, assign it, reply and add notes.
                </p>
              ) : null}
              <ITActions
                ticket={ticket}
                me={me}
                onResolve={() => setResolving(true)}
                resolving={resolving}
              />
            </>
          ) : (
            <>
              <div className="rail__label">Raised</div>
              <div className="rail__value">{stamp(ticket.raisedAt)}</div>
            </>
          )}
        </aside>
      </div>

      {/* IT-only. Renders nothing unless the API attached an assessment, and it
          only does that for IT staff - so there is no role check here and none
          is wanted. INERT TODAY: this page still reads through demo/store.js,
          which is localStorage and never sets `assessment`. It lights up on its
          own the moment the store cuts over to fetch(), with no edit here. The
          server is the boundary; a hidden element would still be in the page
          source. */}
      <SeatAssessmentPanel
        assessment={ticket.assessment}
        turns={ticket.intakeTurns || []}
      />
    </>
  );
}

/* ------------------------------------------------------------------ pieces */

function SlaMeter({ standing, policy, which, paused }) {
  if (!standing) return null;
  const target = policy[which];
  const pct = Math.max(0, Math.min(100, (standing.usedMins / target) * 100));
  const tone = slaTone(standing.state);
  const colour = {
    good: 'var(--ihs-good-ink)',
    warn: 'var(--ihs-warn-ink)',
    error: 'var(--ihs-error-ink)',
    flat: 'var(--ihs-mid-blue)',
  }[tone];

  const left =
    standing.state === 'met'
      ? 'Met'
      : paused
        ? 'Paused'
        : standing.remainingMins < 0
          ? `${fmt(-standing.remainingMins)} over`
          : `${fmt(standing.remainingMins)} left`;

  return (
    <div className="slabox">
      <div className="slabox__top">
        <span className="slabox__left" style={{ color: colour }}>{left}</span>
        <span className="slabox__target">
          {policy.priority} · {formatTarget(target, policy.aroundClock)}
        </span>
      </div>
      <div className="slabar">
        <div className="slabar__fill" style={{ width: `${pct}%`, background: colour }} />
      </div>
    </div>
  );
}

function fmt(mins) {
  const abs = Math.round(mins);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function Details({ ticket, type, files }) {
  const fields = fieldsFor(ticket.requestTypeId);
  return (
    <div className="card">
      <div className="inline" style={{ marginBottom: 10 }}>
        <span className="tk__icon">
          <span className="material-symbols-outlined" aria-hidden="true">{type?.icon || 'help'}</span>
        </span>
        <div>
          <div style={{ font: '500 13px Roboto, sans-serif' }}>{type?.label}</div>
          <div className="muted" style={{ fontSize: 11 }}>{type?.blurb}</div>
        </div>
      </div>

      <div className="tablewrap">
        <table className="table">
          <tbody>
            {/* Asked order, not payload order - the payload is a Snowflake
                VARIANT and a VARIANT sorts its keys, so reading it directly
                showed IT the questions alphabetically. */}
            {answerRows(fields, ticket.formData).map(({ key, field, value }) => (
              <tr key={key}>
                <td style={{ color: 'var(--ihs-ink-3)', width: '48%' }}>{field?.label || key}</td>
                <td>{labelFor(field, value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {ticket.note ? (
        <>
          <div className="rail__label">Their note</div>
          <p style={{ font: '12.5px/1.6 Roboto, sans-serif', whiteSpace: 'pre-wrap' }}>{ticket.note}</p>
        </>
      ) : null}

      {files && files.length ? (
        <>
          <div className="rail__label">Files</div>
          <FileList ticketNo={ticket.number} files={files} />
        </>
      ) : null}

      {ticket.attachments?.length ? (
        <>
          <div className="rail__label">Attached</div>
          <div className="thumbs">
            {ticket.attachments.map((a) => (
              <a key={a.id} className="thumb" href={a.dataUrl} target="_blank" rel="noreferrer">
                <img src={a.dataUrl} alt={a.name} />
              </a>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

/* The P1 "I have this" bar (S10 part C). Staff, real tickets only. Big on purpose: it is tapped
 * one-handed, often from the alert, and it tells the rest of the desk not to start on the same fire.
 * Opening the ticket is NOT acknowledging it. The server decides who wins; the bar shows its answer. */
function AckBar({ ticket }) {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  if (ticket.acknowledgedAt) {
    return (
      <div className="banner banner--good ack ack--done" data-testid="ack-done" role="status">
        <span className="material-symbols-outlined" aria-hidden="true">check_circle</span>
        <span>
          <b>{staffName(ticket.acknowledgedBy) || ticket.acknowledgedBy || 'Someone on the desk'} has this</b> · {clockTime(ticket.acknowledgedAt)}
        </span>
      </div>
    );
  }
  return (
    <div className="banner ack" data-testid="ack-bar">
      <span className="material-symbols-outlined" aria-hidden="true">notifications_active</span>
      <span className="ack__text">
        Nobody has said they have this P1 yet.
        {problem ? <span className="ack__problem" role="alert"> {problem}</span> : null}
      </span>
      <button
        type="button"
        className="btn btn--primary btn--ack"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setProblem(null);
          try {
            await acknowledgeTicket(ticket.number);
          } catch (e) {
            // A 409: resolved or no longer a P1 since the page opened. Pressing again cannot help,
            // so say so and read the ticket again.
            if (e?.status === 409) refresh();
            setProblem(e?.status === 403 ? 'Only the desk that handles this ticket can take it.'
              : e?.status === 409 ? 'This ticket is no longer an open P1.'
                : e?.code === 'NOT_READY' ? 'This is not turned on yet.'
                  : 'That did not go through. Try again.');
          } finally {
            setBusy(false);
          }
        }}
      >
        <span className="material-symbols-outlined" aria-hidden="true">done</span>
        I have this
      </button>
    </div>
  );
}

const SYS_ICON = {
  acknowledge: 'task_alt',
  priority: 'bolt',
  assign: 'person_add',
  status: 'pause_circle',
  reply: 'mail',
  resolve: 'task_alt',
};

function SysLine({ event }) {
  return (
    <div className="sys">
      <span className="material-symbols-outlined" aria-hidden="true">
        {SYS_ICON[event.type] || 'info'}
      </span>
      <span>{event.text}</span>
      <span>· {clockTime(event.at)}</span>
    </div>
  );
}

function Message({ msg, ticket, me, isIT, files }) {
  const mine = msg.authorType === (isIT ? 'it' : 'requester');
  const kind = msg.authorType === 'it' ? 'it' : 'req';
  const roleLabel = msg.authorType === 'it' ? 'IT' : 'Requester';

  return (
    <div className={`msg${msg.isInternal ? ' msg--note' : mine ? ' msg--mine' : ''}`}>
      <Avatar name={msg.author} kind={kind} />
      <div className="msg__bub">
        <div className="msg__who">
          <b>{msg.author === me.name ? 'You' : msg.author}</b>
          {msg.isInternal ? (
            <span className="note-tag">
              <span className="material-symbols-outlined" aria-hidden="true">lock</span>
              Internal note
            </span>
          ) : null}
          <small>
            {roleLabel} · {stamp(msg.at)}
          </small>
        </div>
        <div className="msg__tx">
          {msg.body}
          {msg.attachments?.length ? (
            <div className="msg__file">
              <span className="material-symbols-outlined" aria-hidden="true">image</span>
              {msg.attachments.map((a) => a.name).join(', ')}
            </div>
          ) : null}
          <FileList ticketNo={ticket.number} files={files} />
        </div>
      </div>
    </div>
  );
}

/* The composer.
 *
 * ⚠ The highest-risk control in the product. Reply and internal note are
 * separate TABS, not a checkbox, so choosing between them is deliberate every
 * single time — and the whole composer changes colour when a note is being
 * written. The first time an internal note reaches a requester, IT stops
 * writing honest notes, and the tool loses most of its value.
 */
/* What a finished ticket means for its requester, and what they can do about it (S07b part 3b-2).
 * Grant, 2026-10-22: a plain reply does NOT reopen it; "Still not fixed" does, inside the window;
 * after the window it counts as closed and the page offers a new ticket, linked to this one. */
function RequesterStanding({ ticket, standing }) {
  if (standing === 'open') return null;
  if (standing === 'closed') {
    // Past the window the side panel still says Resolved, so the words say why it takes no replies.
    return (
      <div className="banner" style={{ marginTop: 14 }}>
        <span className="material-symbols-outlined" aria-hidden="true">lock</span>
        <span>
          {ticket.status === 'resolved'
            ? 'The time to reopen this ticket has passed, so it cannot take replies.'
            : 'This ticket is closed, so it cannot take replies.'}{' '}
          If the problem is back, start a new ticket. It will be linked to this one.
        </span>
        <Link to={followUpHref(ticket)} className="btn btn--quiet">Start a new ticket</Link>
      </div>
    );
  }
  return (
    <div className="banner banner--good" style={{ marginTop: 14 }}>
      <span className="material-symbols-outlined" aria-hidden="true">check_circle</span>
      <span>
        Marked resolved {since(ticket.resolvedAt)}.
        {standing === 'reopenable'
          ? ` Still a problem? Write what is still wrong below and press Still not fixed. That sends it back to IT. You can do this until ${stamp(ticket.reopenUntil)}.`
          : ' A reply below still reaches IT.'}
      </span>
    </div>
  );
}

/* The follow-up links (both views). The server lists only tickets this reader may open. */
function FollowUpLinks({ ticket }) {
  const before = ticket.followUpOf || null;
  const after = Array.isArray(ticket.followUps) ? ticket.followUps : [];
  if (!before && after.length === 0) return null;
  return (
    <>
      <div className="rail__label">Linked tickets</div>
      <div className="rail__value">
        {before ? (
          <div>Follow-up of <Link to={`/ticket/${encodeURIComponent(before)}`}>{before}</Link></div>
        ) : null}
        {after.map((n) => (
          <div key={n}>Followed up in <Link to={`/ticket/${encodeURIComponent(n)}`}>{n}</Link></div>
        ))}
      </div>
    </>
  );
}

function Composer({ ticket, isIT, me, denied = false, standing = 'open', onClosed = () => {} }) {
  const [mode, setMode] = useState('reply');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  // One id per draft, re-sent on every retry of that SAME draft. Any change to
  // the words or to reply-vs-note is a different message and gets a new id.
  const [messageId, setMessageId] = useState(() => crypto.randomUUID());
  // The exact submission whose outcome we could not confirm (a lost response).
  // While it is set the draft is frozen and Send repeats THIS payload and id,
  // so the server can answer "already saved" instead of storing a second copy.
  const [doubt, setDoubt] = useState(null);
  // Files to send with this reply (S09). Sent after the reply is saved, naming it.
  const [picks, setPicks] = useState([]);

  /* The requester's closed ticket: the banner above already offers "Start a new ticket", so an EMPTY
   * box goes away. A box with words in it, or with an answer still to read, stays - read-only, with the
   * link - so nothing typed is lost when the window ends mid-sentence or the server says CLOSED
   * (review of #107, F1). An answer still in doubt keeps its Try again: the server finds a saved reply
   * before it checks the window. */
  const closedForMe = !isIT && standing === 'closed' && !doubt;
  if (closedForMe && !body.trim() && !error) return null;
  // The lock banner is for IT (and an empty box). A requester's unsent words stay readable above it.
  if (ticket.status === 'closed' && !closedForMe) {
    return (
      <div className="banner" style={{ marginTop: 16 }}>
        <span className="material-symbols-outlined" aria-hidden="true">lock</span>
        <span>This ticket is closed. Raise a new one and reference {ticket.number} if it comes back.</span>
      </div>
    );
  }

  // The note tab needs the SERVER's say-so on a filed ticket (canNote). The
  // persona switch is only a demo control and must not grant it.
  const canNote = isIT && (!ticket.fromServer || ticket.canNote === true);
  const internal = doubt ? doubt.internal : canNote && mode === 'note';
  const firstName = ticket.requester.name.split(' ')[0];
  // A filed ticket whose thread would not load: writing blind is not safe.
  const blind = Boolean(ticket.threadError) || denied;
  // Words and mode cannot change while a save is in flight (anything typed
  // then would be wiped by the clear below) or while an outcome is in doubt.
  const locked = sending || Boolean(doubt);

  // "Still not fixed" (S07b part 3b-2): the requester's reply that reopens a resolved ticket inside
  // the reopen window. The words say what is still wrong; the server reopens it with them.
  const canStillNotFixed = !isIT && standing === 'reopenable';
  /* Files only on a filed ticket that still takes them. A closed ticket refuses new files on the
   * server (409 CLOSED) even where IT may still save an internal note, because the nightly clean-up
   * would delete them anyway - so the box is not offered there (review of #111, F3). */
  const canAttach = ticket.fromServer && standing !== 'closed' && ticket.status !== 'closed';

  async function send(stillNotFixed = false) {
    if (sending || denied || (!doubt && !body.trim())) return;
    // A retry repeats exactly what was sent, "Still not fixed" included: the server hashes it.
    const sub = doubt || { id: messageId, text: body.trim(), internal, stillNotFixed: stillNotFixed === true && canStillNotFixed };
    setSending(true);
    setError('');
    setNotice('');
    try {
      const receipt = await postMessage(ticket.number, {
        author: me.name,
        authorType: isIT ? 'it' : 'requester',
        isInternal: sub.internal,
        body: sub.text,
        messageId: sub.id,
        source: sourceOf(ticket),
        stillNotFixed: sub.stillNotFixed === true,
      });
      // Only now - after the server said it is saved - does the draft go. The
      // box was locked, so what is cleared is exactly what was submitted.
      setBody('');
      setDoubt(null);
      setMessageId(crypto.randomUUID());
      /* The files, after the reply is saved, naming the id the SERVER gave it (the stored id). A file
       * that does not arrive leaves the reply saved and says which one. */
      // What the SERVER stored decides the words: a note it saved as a normal reply is called a reply,
      // and the warning that the other person can read it is never dropped (review of #111, N4).
      const downgraded = Boolean(receipt?.messageSaved && sub.internal && !receipt.isInternal);
      const what = receipt?.isInternal ? 'Your note' : 'Your reply';
      let filesNotice = '';
      if (receipt?.messageSaved && picks.length && canAttach) {
        const sent = await sendFiles(ticket.number, picks, { messageId: receipt.messageId });
        filesNotice = wordsForFailed(sent.failed, { where: what });
        refresh();
      } else if (receipt?.messageSaved && picks.length) {
        // The ticket closed while the files were picked: the server would refuse them (review of #111, N2).
        filesNotice = `${what} is saved, but the ticket has closed, so the files were not added.`;
      }
      setPicks([]);
      const notices = [];
      if (downgraded) notices.push('Saved as a normal reply, not an internal note — the server does not see you as IT staff. The other person can read it.');
      if (filesNotice) notices.push(filesNotice);
      else if (receipt?.messageSaved && !downgraded && receipt.emailStatus === 'failed') {
        notices.push('Your message is saved and shows in the conversation. The email telling them about it could not be sent — do not send it again.');
      }
      if (notices.length) setNotice(notices.join(' '));
    } catch (err) {
      if (err?.code === WALKTHROUGH_WRITE_BLOCKED) {
        // Refused before anything was sent: not "unconfirmed", and nothing to retry.
        setDoubt(null);
        setError(`${err.message} Your text is still here.`);
      } else if (err?.outcome === 'rejected' && err.code === 'CLOSED') {
        // Closed, or the reopen window ran out while the page was open. Nothing was saved; the page
        // re-reads and then offers "Start a new ticket". The words stay so they can be copied.
        setDoubt(null);
        setMessageId(crypto.randomUUID());
        if (!isIT) onClosed();
        refresh();
        setError(isIT
          ? `Your reply was NOT saved. This ticket is closed, so a reply would not reach anyone.${ticket.status === 'closed' ? '' : ' You can still add an internal note.'}`
          : 'Your message was NOT saved. This ticket is closed now. Copy your text, then start a new ticket. It will be linked to this one.');
      } else if (err?.outcome === 'rejected' && err.code === 'NOT_READY') {
        setDoubt(null);
        setMessageId(crypto.randomUUID());
        setError('Your message was NOT saved. This is not turned on yet for this ticket. Your text is still here.');
      } else if (err?.outcome === 'rejected') {
        setDoubt(null);
        // A 409 means this id already belongs to a different message.
        if (err.status === 409) setMessageId(crypto.randomUUID());
        setError(sub.internal && err.status === 403
          ? 'Your note was NOT saved. This account is not allowed to write internal notes, so nothing was stored or sent. Your text is still here. To share it as a normal reply, switch to Reply and press Send.'
          : 'Your message was NOT saved. Your text is still here \u2014 press Send again.');
      } else {
        setDoubt(sub);
        refresh(); // so the thread above shows it if it did land
        setError('We could not confirm whether this was saved. Check the conversation above. If it is not there, press Try again once and wait for the answer.');
      }
    } finally {
      setSending(false);
    }
  }

  return (
    <div className={`comp${internal ? ' comp--note' : ''}`}>
      {canNote ? (
        <div className="comp__tabs">
          <button
            type="button"
            className={`comp__tab${mode === 'reply' ? ' comp__tab--on' : ''}`}
            disabled={locked || denied}
            onClick={() => { setMode('reply'); setMessageId(crypto.randomUUID()); }}
          >
            Reply to {firstName}
          </button>
          <button
            type="button"
            className={`comp__tab comp__tab--note${mode === 'note' ? ' comp__tab--on' : ''}`}
            disabled={locked || denied}
            onClick={() => { setMode('note'); setMessageId(crypto.randomUUID()); }}
          >
            Internal note
          </button>
        </div>
      ) : null}

      <textarea
        className="comp__in"
        placeholder={
          internal
            ? 'Only IT sees this. Say what you actually think.'
            : isIT
              ? `Write your reply to ${firstName}…`
              : 'Add anything that helps, or answer what IT asked…'
        }
        value={doubt ? doubt.text : body}
        readOnly={closedForMe}
        aria-label={closedForMe ? 'Your text (not sent)' : undefined}
        disabled={blind || locked}
        onChange={(e) => {
          setBody(e.target.value);
          // Edited text is a different message: it must not collapse into a
          // save of the old words on the server.
          setMessageId(crypto.randomUUID());
        }}
      />

      {canAttach && !closedForMe ? (
        <FilePicker picks={picks} onChange={setPicks} disabled={blind || locked} label="Add a screenshot or file" />
      ) : null}

      {error ? (
        <div className="banner banner--bad" role="alert" style={{ marginTop: 8 }}>
          <span className="material-symbols-outlined" aria-hidden="true">error</span>
          <span>{error}</span>
          {doubt && !sending ? (
            <button
              type="button"
              className="btn btn--quiet"
              onClick={() => {
                // Give up on the old attempt: the text becomes an ordinary
                // draft with a new id. Only sensible once the thread above has
                // been checked - otherwise it can post a second copy.
                setBody(doubt.text);
                setMode(doubt.internal ? 'note' : 'reply');
                setDoubt(null);
                setError('');
                setMessageId(crypto.randomUUID());
              }}
            >
              Edit as a new message
            </button>
          ) : null}
        </div>
      ) : null}
      {notice ? (
        <div className="banner banner--info" role="status" style={{ marginTop: 8 }}>
          <span className="material-symbols-outlined" aria-hidden="true">info</span>
          <span>{notice}</span>
        </div>
      ) : null}

      <div className="comp__bar">
        {ticket.fromServer ? null : (
          <span
            className="material-symbols-outlined"
            title="Attaching to a reply is not in this prototype — images arrive at intake for now."
          >
            attach_file
          </span>
        )}
        {closedForMe ? (
          <Link to={followUpHref(ticket)} className="btn btn--primary">Start a new ticket</Link>
        ) : null}
        {canStillNotFixed && !doubt ? (
          <button type="button" className="btn btn--quiet" onClick={() => send(true)} disabled={sending || denied || blind || !body.trim()}>
            Still not fixed
          </button>
        ) : null}
        {closedForMe ? null : (
          <button type="button" className="btn btn--primary" onClick={() => send(false)} disabled={sending || denied || (blind && !doubt) || (!doubt && !body.trim())}>
            {sending ? 'Sending…' : doubt ? 'Try again' : internal ? 'Save note' : 'Send reply'}
          </button>
        )}
      </div>

      <div className="comp__hint">
        <span className="material-symbols-outlined" aria-hidden="true">
          {internal ? 'lock' : 'mail'}
        </span>
        {internal
          ? `Not visible to ${ticket.requester.name}.`
          : isIT
            ? `${firstName} gets an email saying there is a reply. They answer here, not by email.`
            : closedForMe
              ? 'This was not sent. Copy it into a new ticket.'
              : canStillNotFixed
                ? 'Send reply tells IT and keeps it resolved. Still not fixed sends it back to IT.'
                : 'Replying puts this back in front of IT.'}
      </div>
    </div>
  );
}

function AssigneeControl({ ticket, me, meStaffId, reference }) {
  const commands = useCommands(ticket);
  const live = ticket.fromServer && ticket.canCommand === true;
  const [people, setPeople] = useState(null); // filed tickets: [{ upn }] this ticket may be given to
  useEffect(() => {
    if (!live) return undefined;
    let cancelled = false;
    listAssignees(ticket.number)
      .then((list) => { if (!cancelled) setPeople(list); })
      .catch(() => { if (!cancelled) setPeople([]); });
    return () => { cancelled = true; };
  }, [live, ticket.number, ticket.version]);

  if (!live) {
    return (
      <>
        <select
          className="rail__sel"
          value={ticket.assignee || ''}
          disabled={ticket.fromServer}
          onChange={(e) => updateTicket(ticket.number, { assignee: e.target.value || null }, { by: me.name, source: sourceOf(ticket) })}
        >
          <option value="">Nobody yet</option>
          {(reference?.staff || []).map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        {!ticket.assignee ? (
          <button
            type="button"
            className="btn btn--primary btn--block"
            style={{ marginTop: 8 }}
            disabled={ticket.fromServer}
            onClick={() => updateTicket(ticket.number, { assignee: meStaffId }, { by: me.name, source: sourceOf(ticket) })}
          >
            Take it
          </button>
        ) : null}
      </>
    );
  }

  const closed = ['resolved', 'closed'].includes(ticket.status);
  const mine = String(me.email || '').toLowerCase();
  const listed = (people || []).map((p) => p.upn);
  const options = ticket.assignee && !listed.includes(ticket.assignee) ? [ticket.assignee, ...listed] : listed;
  // "Take it" starts the work while the ticket is new; on one already under way it only assigns it.
  const take = () => (ticket.status === 'new' ? commands.run('start', {}) : commands.run('assign', { assignee: mine }));
  return (
    <>
      <select
        className="rail__sel"
        value={ticket.assignee || ''}
        disabled={commands.busy || closed || people === null}
        onChange={(e) => commands.run('assign', { assignee: e.target.value || null })}
      >
        <option value="">Nobody yet</option>
        {options.map((upn) => (
          <option key={upn} value={upn}>{upn}</option>
        ))}
      </select>
      {!ticket.assignee && !closed ? (
        <button type="button" className="btn btn--primary btn--block" style={{ marginTop: 8 }} disabled={commands.busy} onClick={take}>
          Take it
        </button>
      ) : null}
      <CommandNotice error={commands.error} />
    </>
  );
}

function PriorityControl({ ticket, me }) {
  const [pending, setPending] = useState(null);
  const [reason, setReason] = useState('');
  const commands = useCommands(ticket);
  const closed = ['resolved', 'closed'].includes(ticket.status);
  const allowed = canTriage(ticket) && !commands.busy && !(ticket.fromServer && closed);

  return (
    <>
      <select
        className="rail__sel"
        value={pending ?? ticket.priority}
        disabled={!allowed}
        onChange={(e) => setPending(e.target.value === ticket.priority ? null : e.target.value)}
      >
        {['P1', 'P2', 'P3', 'P4'].map((p) => (
          <option key={p} value={p}>{p}</option>
        ))}
      </select>

      {pending ? (
        <div style={{ marginTop: 8 }}>
          <span className="field__label">
            Why {ticket.priority} → {pending}?
          </span>
          <textarea
            className="textarea"
            style={{ minHeight: 54 }}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <div className="inline" style={{ marginTop: 6 }}>
            <button
              type="button"
              className="btn btn--primary"
              disabled={!reason.trim() || commands.busy}
              onClick={async () => {
                if (ticket.fromServer) {
                  const saved = await commands.run('priority', { priority: pending, reason: reason.trim() });
                  if (!saved) return; // keep the words they typed; the notice says why
                } else {
                  await updateTicket(ticket.number, { priority: pending, overrideReason: reason.trim() }, { by: me.name, source: sourceOf(ticket) });
                }
                setPending(null);
                setReason('');
              }}
            >
              Change it
            </button>
            <button type="button" className="btn btn--ghost" onClick={() => { setPending(null); setReason(''); }}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}
      <CommandNotice error={commands.error} />
    </>
  );
}

/* Hold, resume, resolve and reopen. A filed ticket runs them through the server's commands (S07b),
 * only when the server says it takes commands (canCommand) AND its database is ready for them
 * (lifecycleReady); a demo ticket changes the demo store. */
function ITActions({ ticket, me, onResolve, resolving }) {
  const [holding, setHolding] = useState(false);
  const [reasonValue, setReasonValue] = useState('requester');
  const commands = useCommands(ticket);
  const live = ticket.fromServer && ticket.canCommand === true && ticket.lifecycleReady === true;
  const off = (ticket.fromServer && !live) || commands.busy;
  const closed = ['resolved', 'closed'].includes(ticket.status);

  if (closed) {
    return ticket.fromServer
      ? <ReopenControl ticket={ticket} live={live} />
      : (
        <p className="why" style={{ marginTop: 0 }}>
          Resolved {since(ticket.resolvedAt)}. It closes itself five days after that, and the
          message thread is deleted at close — the summary is what survives.
        </p>
      );
  }

  const resume = () => (live
    ? commands.run('resume', {})
    : updateTicket(ticket.number, { status: 'in_progress' }, { by: me.name, source: sourceOf(ticket) }));
  const hold = async () => {
    const saved = live
      ? await commands.run('hold', { reason: reasonValue })
      : (await updateTicket(ticket.number, { status: 'waiting', waitingReason: reasonValue }, { by: me.name, source: sourceOf(ticket) }), true);
    if (saved) setHolding(false);
  };

  return (
    <div className="stack">
      {ticket.status === 'waiting' ? (
        <button
          type="button"
          className="btn btn--quiet btn--block"
          disabled={off}
          onClick={resume}
        >
          <span className="material-symbols-outlined" aria-hidden="true">play_circle</span>
          Pick it back up
        </button>
      ) : (
        <button type="button" className="btn btn--quiet btn--block" disabled={off} onClick={() => setHolding(!holding)}>
          <span className="material-symbols-outlined" aria-hidden="true">pause_circle</span>
          Put on hold
        </button>
      )}

      {holding ? (
        <div>
          <span className="field__label">What is it waiting on?</span>
          <div className="stack" style={{ gap: 6 }}>
            {WAITING_REASONS.map((r) => (
              <button
                key={r.value}
                type="button"
                className={`o${reasonValue === r.value ? ' o--on' : ''}`}
                onClick={() => setReasonValue(r.value)}
              >
                {r.label}
              </button>
            ))}
            <button
              type="button"
              className="btn btn--primary"
              disabled={off}
              onClick={hold}
            >
              Put on hold
            </button>
          </div>
          <p className="why">
            Either way the clock pauses. This tells the queue which it is.
            {reasonValue === 'requester' ? ' Their next reply takes it off hold by itself.' : ''}
          </p>
        </div>
      ) : null}

      <button type="button" className="btn btn--primary btn--block" onClick={onResolve} disabled={resolving || off}>
        <span className="material-symbols-outlined" aria-hidden="true">task_alt</span>
        Resolve ticket
      </button>
      <CommandNotice error={commands.error} />
    </div>
  );
}

/* A resolved FILED ticket: IT may reopen it inside the reopen window, with a reason. After the window
 * it counts as closed: the requester is offered a new ticket, linked to this one. The server decides;
 * this only offers what it will accept. */
function ReopenControl({ ticket, live }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const commands = useCommands(ticket);
  // Set when Reopen is pressed after the deadline passed with the page open: the page was drawn
  // before it, so it is checked again at the press.
  const [expired, setExpired] = useState(false);
  const until = ticket.reopenUntil ? new Date(ticket.reopenUntil) : null;
  const pastNow = () => until !== null && until.getTime() < Date.now();
  const past = ticket.status === 'closed' || expired || pastNow();

  if (past) {
    return (
      <p className="why" style={{ marginTop: 0 }}>
        Resolved {since(ticket.resolvedAt)} and now closed. If it comes back, the requester starts a
        new ticket, and it is linked to this one.
      </p>
    );
  }
  return (
    <div className="stack">
      <p className="why" style={{ marginTop: 0 }}>
        Resolved {since(ticket.resolvedAt)}.
        {until ? ` It can be reopened until ${stamp(ticket.reopenUntil)}.` : ''}
      </p>
      {!open ? (
        <button type="button" className="btn btn--quiet btn--block" disabled={!live || commands.busy} onClick={() => (pastNow() ? setExpired(true) : setOpen(true))}>
          <span className="material-symbols-outlined" aria-hidden="true">undo</span>
          Reopen
        </button>
      ) : (
        <div>
          <span className="field__label" id="reopen-why">Why is it being reopened?</span>
          <textarea
            className="textarea"
            style={{ minHeight: 54 }}
            aria-labelledby="reopen-why"
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <div className="inline" style={{ marginTop: 6 }}>
            <button
              type="button"
              className="btn btn--primary"
              disabled={reason.trim().length < 3 || commands.busy}
              onClick={async () => {
                if (await commands.run('reopen', { reason: reason.trim() })) {
                  setOpen(false);
                  setReason('');
                }
              }}
            >
              Reopen it
            </button>
            <button type="button" className="btn btn--ghost" onClick={() => { setOpen(false); setReason(''); }}>
              Cancel
            </button>
          </div>
        </div>
      )}
      <CommandNotice error={commands.error} />
    </div>
  );
}

/* Resolve.
 *
 * AI drafts the write-up and it SAVES ITSELF — review is after the fact. A
 * manual write-up step is demonstrably what gets skipped when the queue is
 * busy: the Asana board has had a `Resolution Made` field all along and it is
 * empty on all 412 finished tickets.
 *
 * In the prototype the "model" is draftSummary() below — a stand-in that
 * assembles a plausible write-up from the ticket so the SHAPE and the
 * save-then-review flow can be argued with. The real one reads the whole
 * thread, internal notes included, which is why the summary is IT-visible only.
 */
function ResolvePanel({ ticket, me, onDone }) {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const commands = useCommands(ticket);

  // Keyed on the ticket NUMBER, not the object. The object is re-created on
  // every store read, and depending on it would wipe an edit in progress.
  useEffect(() => {
    const timer = setTimeout(() => setDraft(draftSummary(ticket)), 700);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket.number]);

  return (
    <div className="ai" style={{ marginTop: 16 }}>
      <div className="ai__head">
        <span className="material-symbols-outlined" aria-hidden="true">auto_awesome</span>
        Drafted from this ticket
        <Chip tone="flat" icon="lock">IT only</Chip>
      </div>

      {!draft ? (
        <div className="ai__txt muted">Reading the thread, internal notes included…</div>
      ) : (
        <>
          <div className="ai__txt">
            {[
              ['Problem', 'problem'],
              ['Cause', 'cause'],
              ['Fix', 'fix'],
              ['Watch for', 'watchFor'],
            ].map(([label, key]) => (
              <div key={key} style={{ marginBottom: 8 }}>
                <b id={`summary-${key}`}>{label}.</b>
                <textarea
                  className="textarea"
                  style={{ minHeight: 46, marginTop: 4 }}
                  aria-labelledby={`summary-${key}`}
                  maxLength={SUMMARY_PART_MAX}
                  placeholder={DRAFT_HINTS[key]}
                  value={draft[key]}
                  onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
                />
              </div>
            ))}
          </div>

          <p className="why" style={{ marginTop: 10 }}>
            It saves with the resolve — correct it now or later, but nothing blocks you closing
            this out. A write-up that needs a sign-off is a write-up that gets skipped.
            {ticket.fromServer ? ' The Fix part must say what fixed it.' : ''}
          </p>

          <div className="inline" style={{ marginTop: 10 }}>
            <button
              type="button"
              className="btn btn--primary"
              disabled={saving || commands.busy || (ticket.fromServer && draft.fix.trim().length < 3)}
              onClick={async () => {
                if (ticket.fromServer) {
                  // Kept on screen when it did not save: the notice says why, and nothing typed is lost.
                  if (await commands.run('resolve', { summary: draft })) onDone();
                  return;
                }
                setSaving(true);
                await resolveTicket(ticket.number, { by: me.name, summary: draft, source: sourceOf(ticket) });
                onDone();
              }}
            >
              {saving || commands.busy ? 'Resolving…' : 'Save & resolve'}
            </button>
            <button type="button" className="btn btn--quiet" onClick={onDone}>
              Cancel
            </button>
          </div>
          <CommandNotice error={commands.error} />
        </>
      )}
    </div>
  );
}

// The server's limit for each part of the summary (SUMMARY_MAX in api/src/shared/ticketCommand.js).
const SUMMARY_PART_MAX = 2000;

/* Shown in an empty box, never put in it: a hint left in the box would be saved as the cause or
 * the fix, and the 3-character check could not tell it from a real one. */
const DRAFT_HINTS = {
  cause: 'Not established in the thread — fill this in.',
  fix: 'Describe what actually fixed it.',
};

function draftSummary(ticket) {
  const type = typeById(ticket.requestTypeId);
  const fields = fieldsFor(ticket.requestTypeId);
  /* The FIRST THREE QUESTIONS, not the first three alphabetically. This feeds
   * the reply IT drafts, and the payload's own key order is a Snowflake
   * VARIANT's sorted order - which put the acknowledgement and the branding
   * question at the front of every seat request's summary. */
  const answered = answerRows(fields, ticket.formData)
    .filter((r) => r.field)
    .map((r) => `${r.field.label.replace(/\?$/, '')}: ${String(labelFor(r.field, r.value)).toLowerCase()}`)
    .slice(0, 3)
    .join('; ');

  const itSaid = ticket.messages.filter((m) => m.authorType === 'it' && !m.isInternal).map((m) => m.body);
  const notes = ticket.messages.filter((m) => m.isInternal).map((m) => m.body);

  const who = [ticket.requester.role, ticket.requester.brand].filter(Boolean).join(', ');
  // Each part fits the server's limit (SUMMARY_MAX in api ticketCommand.js): a long note or reply is
  // cut here rather than refused at the save. maxLength on the box only stops typing.
  const fit = (text) => String(text).slice(0, SUMMARY_PART_MAX);
  return {
    problem: fit(`${ticket.requester.name}${who ? ` (${who})` : ''} reported: ${type?.label.toLowerCase()}. ${answered}.${ticket.note ? ` They added: "${ticket.note}"` : ''}`),
    cause: notes.length ? fit(notes[notes.length - 1]) : '',
    fix: itSaid.length ? fit(itSaid[itSaid.length - 1]) : '',
    watchFor:
      ticket.formData.othersAffected && ticket.formData.othersAffected !== 'just_me'
        ? 'More than one person was affected — check whether the others were picked up separately.'
        : 'Nothing noted.',
  };
}

/* `reopened`: the ticket is open again, so this is the LAST fix, the one that did not hold. IT still
 * wants it (Grant, 2026-10-23). */
function Summary({ summary, reopened = false }) {
  return (
    <div className="ai" style={{ marginTop: 14 }}>
      <div className="ai__head">
        <span className="material-symbols-outlined" aria-hidden="true">auto_awesome</span>
        {reopened ? 'Last resolution summary' : 'Resolution summary'}
        <Chip tone="flat" icon="lock">IT only</Chip>
        {!summary.reviewed ? <Chip tone="warn">Not reviewed yet</Chip> : null}
      </div>
      <div className="ai__txt">
        <b>Problem.</b> {summary.problem || '—'}
        <br />
        <b>Cause.</b> {summary.cause || '—'}
        <br />
        <b>Fix.</b> {summary.fix || '—'}
        <br />
        <b>Watch for.</b> {summary.watchFor || '—'}
        {summary.by ? (
          <>
            <br />
            <span className="muted">Resolved by {summary.by}{summary.at ? ` · ${stamp(summary.at)}` : ''}</span>
          </>
        ) : null}
      </div>
      <p className="why" style={{ marginTop: 10 }}>
        {reopened
          ? 'This ticket was reopened after this fix. A new summary is written when it is resolved again.'
          : 'This is the permanent record. Once the ticket closes the message thread is deleted and this is what is left — and what the IT assistant learns from later.'}
      </p>
    </div>
  );
}
